import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import { Building2, HeartPulse, TrendingUp, TrendingDown, Lock } from 'lucide-react';

const fmt0 = (v) => `$${(Number(v) || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function CashFlowPositionSummary({ BACKEND_URL, view, year, month, rangeStart, rangeEnd }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ view });
      if (year) params.set('year', String(year));
      if (month) params.set('month', String(month));
      if (rangeStart) params.set('start_date', rangeStart);
      if (rangeEnd) params.set('end_date', rangeEnd);
      const res = await fetch(`${BACKEND_URL}/api/super-admin/financials/cash-flow-position-summary?${params}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [BACKEND_URL, view, year, month, rangeStart, rangeEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) return <Card className="p-6"><div className="animate-pulse h-48 bg-slate-100 rounded" /></Card>;
  const { company, personal } = data;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="cash-flow-position-summary">
      <PositionPie
        title="Company Cash Flow Position"
        icon={Building2}
        iconColor="text-blue-600"
        bg="from-blue-50 to-cyan-50"
        block={company}
        testid="company-position-pie"
      />
      <PositionPie
        title="Personal Cash Flow Position"
        icon={HeartPulse}
        iconColor="text-indigo-600"
        bg="from-indigo-50 to-purple-50"
        block={personal}
        testid="personal-position-pie"
        private_={true}
        noDataMsg={!personal.has_data ? 'Add personal income/expenses on the Personal Finance card to see your position here.' : null}
      />
    </div>
  );
}

function PositionPie({ title, icon: Icon, iconColor, bg, block, testid, private_, noDataMsg }) {
  const segments = (block.pie_segments || []).filter(s => s.value > 0);
  const isProfit = block.net_profit_loss >= 0;
  return (
    <Card className={`p-5 bg-gradient-to-br ${bg}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${iconColor}`} />
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          {private_ && <span className="text-[10px] uppercase tracking-wider text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded flex items-center gap-1"><Lock className="h-2.5 w-2.5" /> private</span>}
        </div>
      </div>

      {noDataMsg ? (
        <div className="text-center py-12 text-sm text-slate-500 border-2 border-dashed border-slate-200 rounded">
          {noDataMsg}
        </div>
      ) : segments.length === 0 ? (
        <div className="text-center py-12 text-sm text-slate-500">
          No data for this period yet.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 items-center">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={segments} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={80} innerRadius={45}>
                  {segments.map((s, i) => <Cell key={i} fill={s.color} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt0(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2 text-sm">
              <Stat label="Assets" value={fmt0(block.assets)} color="text-emerald-700" />
              <Stat label="Liabilities" value={fmt0(block.liabilities)} color="text-rose-700" />
              <div className={`p-3 rounded-lg ${isProfit ? 'bg-emerald-100' : 'bg-rose-100'} border ${isProfit ? 'border-emerald-300' : 'border-rose-300'}`}>
                <p className={`text-[10px] uppercase tracking-wider font-bold ${isProfit ? 'text-emerald-700' : 'text-rose-700'} flex items-center gap-1`}>
                  {isProfit ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  Net {isProfit ? 'Profit' : 'Loss'}
                </p>
                <p className={`text-xl font-black ${isProfit ? 'text-emerald-900' : 'text-rose-900'}`}>
                  {!isProfit && '−'}{fmt0(Math.abs(block.net_profit_loss))}
                </p>
              </div>
            </div>
          </div>
          {block.breakdown && (
            <div className="mt-3 pt-3 border-t border-slate-200/70 text-[11px] text-slate-600 flex flex-wrap gap-x-3 gap-y-1">
              {Object.entries(block.breakdown).map(([k, v]) => (
                <span key={k}>
                  {k.replace(/_/g, ' ')}: <strong>{fmt0(v)}</strong>
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`text-lg font-bold ${color}`}>{value}</p>
    </div>
  );
}
